import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from '../../auth/services/auth.service';
import { ToastService } from '../../shared/services/toast.service';
import { skipLoading } from '../../core/auth-context';

const apiUrl = environment.apiUrl;
@Injectable({
  providedIn: 'root'
})
export class ShopService {

  http = inject(HttpClient);
  router = inject(Router);
  authService = inject(AuthService);
  toastService = inject(ToastService);

  private initialized = false;
  private cachedData: any = {};


  carts = signal<any[]>([]);

  init(): void {

    if (this.initialized) return;
    this.refreshCarts();
    this.initialized = true;
  }

  getListingRootProductCategories(listings: string[], _skipLoading: boolean = false): Observable<any[]> {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    return this.http.post(`${apiUrl}/inventory/getListingRootProductCategories`, {
      inventoryListingIds: listings,
      paging: {
        pageNumber: 1,
        rows: 50
      },
    }, { context }).pipe(
      tap((res: any) => {
        this.setCachedData('productCategories', (res?.data?.productCategories || []));
      }),
      map((res: any) => res?.data?.productCategories || [])
    );
  }

  getProductCategoriesByParentId(parentId: string | null = null, _skipLoading: boolean = false): Observable<any[]> {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    return this.http.post(`${apiUrl}/products/getProductCategories`, {
      parentId: parentId,
      orderBy: 'name',
      paging: {
        pageNumber: 1,
        rows: 500
      }
    }, { context }).pipe(
      tap((res: any) => {
        this.setCachedData('productCategories', (res?.categories || []));
      }),
      map((res: any) => res?.categories || [])
    );
  }

  getInventoryListings(): Observable<any[]> {

    return this.http.post(`${apiUrl}/inventory/getListings`, {
      orderBy: 'name',
      paging: {
        pageNumber: 1,
        rows: 50
      }
    }).pipe(
      tap((res: any) => {
        this.setCachedData('listings', res?.data?.listings || []);
      }),
      map((res: any) => res?.data?.listings || [])
    );
  }

  getProductListingItems(params: any, _skipLoading: boolean = false) {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    const searchText = params.searchText ?? '';
    const shippingDate = params.shippingDate ?? new Date().toISOString();
    const customerAccountId = params.customerAccount;
    const productCategoryPaths = params.productCategories ?? [];
    const listings = params.listings ?? [];
    const take = params.take || 50;
    const skip = params.skip || 0;
    const id = params.id || null;

    const req: any = {
      shippingDate: shippingDate,
      inventoryListingIds: listings,
      searchText: searchText,
      productCategoryPaths: productCategoryPaths,
      productCategoryPath: productCategoryPaths,
      accountId: customerAccountId,
      paging: {
        pageNumber: skip,
        rows: take
      }
    }

    if (id) {
      req.ids = [id];
    }

    return this.http.post(`${apiUrl}/trading/getSalesMarketplaceItems`, req, { context });
  }

  getCustomerAccounts(options: any, _skipLoading: boolean = false): Observable<any[]> {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    return this.http.post(`${apiUrl}/trading/getCustomerAccounts`, {
      id: options.id || null,
      name: options.searchText,
      isActive: true,
      includeShippingLocations: true,
      orderBy: 'name',
      paging: {
        pageNumber: 1,
        rows: 300
      }
    }, { context }).pipe(
      map((res: any) => res?.customerAccounts || [])
    );
  }

  setCachedData(collectionName: string, items: any[], idField: string = 'id') {

    if (!items.length) return;

    let collectionItems = this.cachedData[collectionName] || [];
    const ids = collectionItems.map((o: any) => o[idField]);

    if (ids.length) {
      for (const item of items) {
        if (!ids.includes(item[idField])) {
          collectionItems.push(item);
        }
      }
    }
    else {
      collectionItems = [...items];
    }

    this.cachedData[collectionName] = collectionItems;
  }

  getCachedData(collection: string, values: string[], field: string = 'id') {
    return this.cachedData[collection]?.filter((o: any) => values.includes(o[field])) || [];
  }


  // Shopping Cart

  refreshCarts(_skipLoading: boolean = false) {

    this.getCarts({}, _skipLoading).subscribe((res) => {

      const resCarts = (res?.data?.shoppingCarts || []).map((o: any) => {
        return {
          ...o.header,
          items: o.items
        }
      });

      this.carts.set(resCarts);
    });
  }

  getCarts(request: any, _skipLoading: boolean = false): Observable<any> {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    return this.http.post(`${apiUrl}/trading/getShoppingCarts`,
      {
        userId: this.authService.user().id,
        shoppingCartIds: request.ids || [],
        paging: {
          pageNumber: 1,
          rows: 10
        }
      }, { context }
    )
  }

  deleteCarts(ids: string[] = []): Observable<any> {

    return this.http.post(`${apiUrl}/trading/deleteShoppingCarts`,
      {
        shoppingCartIds: ids
      }
    ).pipe(
      finalize(() => {
        this.refreshCarts();
      })
    )
  }

  deleteCartItems(request: any): Observable<any> {

    return this.http.post(`${apiUrl}/trading/deleteShoppingCartItems`,
      {
        shoppingCartId: request.shoppingCartId,
        shoppingCartItemIds: request.ids
      }
    ).pipe(
      finalize(() => {
        this.refreshCarts();
      })
    )
  }

  checkOutCarts(request: any) {

    return this.http.post(`${apiUrl}/trading/checkOutShoppingCarts`,
      {
        shoppingCartIds: request.ids
      }
    ).pipe(
      tap((res: any) => {

        if (res?.transaction?.number) {
          this.toastService.show(`${res?.transaction?.number}`,'success');
        }
      }),
      finalize(() => this.refreshCarts())
    )
  }

  addToCart(request: any, _skipLoading: boolean = false): Observable<any> {

    const context = new HttpContext().set(skipLoading, _skipLoading);

    return this.http.post(`${apiUrl}/trading/saveShoppingCarts`,
      {
        userId: this.authService.user().id,
        shippingDate: request.shippingDate,
        accountId: request.customerAccount,
        destinationLocationId: request.destinationLocation,
        carrierId: "ec528463-e444-11e8-88c5-b39ca7534a35",
        reference: "Prueba WebShop",
        items: request.items
      }, { context }
    ).pipe(
      finalize(() => {
        this.refreshCarts(true);
      })
    )
  }

  // orders

  getSalesOrders(request: any): Observable<any> {
    
    return this.http.post(`${apiUrl}/trading/getSalesOrders`,
      {
        ...request
      }
    )
  }
}
